import React, { useState } from "react";
import "./Sidebar.scss";
import { capitalizeFirst } from "utils/Common/String";
import Button from "Components/Common/Button/Button";

interface SidebarBadge {
  text: string;
  variant?: string;
}

interface SidebarSubItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: SidebarBadge;
  versionNumber?: number;
}

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: SidebarBadge;
  subItems?: SidebarSubItem[];
}

interface SidebarSection {
  label: string;
  items: SidebarItem[];
}

interface SidebarProps {
  sections?: SidebarSection[];
  activeSection?: string;
  onSelect: (id: string) => void;
  isCollapsed?: boolean;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
}

const Sidebar = ({
  sections = [],
  activeSection,
  onSelect,
  isCollapsed = false,
  headerContent,
  footerContent,
}: SidebarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string>("");

  const handleItemClick = (item: SidebarItem) => {
    if (item.subItems && item.subItems.length > 0) {
      const nextExpanded = expandedId === item.id ? "" : item.id;
      setExpandedId(nextExpanded);
      if (nextExpanded && item.subItems[0]) {
        onSelect(item.subItems[0].id);
      }
    } else {
      setExpandedId("");
      onSelect(item.id);
    }
    setIsOpen(false);
  };

  return (
    <>
      {/*  <Button className="hamburgerBtn" onClick={() => setIsOpen(!isOpen)}>
        ☰
       </Button> */}
      <aside
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isOpen ? "open" : ""}`}
      >
        <Button className="hamburgerBtn" onClick={() => setIsOpen(!isOpen)}>
          ☰
        </Button>
        <div className="sidebarContent">
          {headerContent && (
            <div className="sidebarHeader">{headerContent}</div>
          )}
          {sections.map((group) => (
            <div key={group.label} className="sidebarGroup">
              <div className="sidebarSectionLabel">{group.label}</div>

              {group.items.map((item) => {
                const hasSubItems = item.subItems && item.subItems.length > 0;
                const isExpanded = expandedId === item.id;
                const isParentActive =
                  hasSubItems &&
                  item.subItems!.some((s) => s.id === activeSection);

                return (
                  <React.Fragment key={item.id}>
                    <Button
                      className={`sidebarItem ${
                        !hasSubItems && activeSection === item.id
                          ? "active"
                          : ""
                      } ${isParentActive ? "activeParent" : ""}`}
                      onClick={() => handleItemClick(item)}
                    >
                      <span className="sidebarIcon">{item.icon}</span>
                      <span className="sidebarText">{item.label}</span>
                      {item.badge && (
                        <span className="sidebarBadge">{item.badge.text}</span>
                      )}
                      {hasSubItems && (
                        <span
                          className={`sidebarChevron${isExpanded ? " up" : ""}`}
                        >
                          ›
                        </span>
                      )}
                    </Button>

                    {hasSubItems &&
                      isExpanded &&
                      item.subItems!.map((sub) => (
                        <Button
                          key={sub.id}
                          className={`sidebarSubItem ${activeSection === sub.id ? "active" : ""}`}
                          onClick={() => {
                            onSelect(sub.id);
                            setIsOpen(false);
                          }}
                        >
                          <span
                            className={`textOnOpen ${sub.badge?.variant || ""}`}
                          >
                            {sub.versionNumber ? `V${sub.versionNumber}` : ""}
                          </span>

                          {sub.icon && (
                            <span className="sidebarSubIcon">{sub.icon}</span>
                          )}
                          <span className="sidebarText">{sub.label}</span>
                          {sub.badge && (
                            <span
                              className={`sidebarBadge ${sub.badge.variant || ""}`}
                            >
                              {capitalizeFirst(sub.badge.text)}
                            </span>
                          )}
                        </Button>
                      ))}
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>
        {footerContent && <div className="sidebarFooter">{footerContent}</div>}
      </aside>
    </>
  );
};

export default Sidebar;
